<script lang="ts" setup>
import { computed } from "vue";
import { currencyFormat } from "~/shared/util";

interface IndividualRollingRequirement {
    required: number;
    progress: number;
    eligible: boolean;
}

interface CouponRollingDetails {
    activeCouponId: string;
    rollingRequired: number;
    rollingProgress: number;
    cycleEligible: boolean;
    individualRollingRequirements?: {
        sport: IndividualRollingRequirement;
        casino: IndividualRollingRequirement;
        minigame: IndividualRollingRequirement;
    };
}

const props = defineProps<{
    details: CouponRollingDetails;
}>();

const emits = defineEmits(["close"]);

const handleClose = () => {
    emits("close");
};

const calculatePercentage = (progress: number, required: number) => {
    if (isNaN(progress) || isNaN(required) || required === 0) {
        return 0;
    }
    return Math.min(100, (progress / required) * 100);
};

// 개별 롤링 요구사항이 있는지 확인
const hasIndividualRequirements = computed(() => {
    return props.details.individualRollingRequirements !== undefined;
});

// 전체 진행률 계산
const totalProgress = computed(() => {
    return calculatePercentage(props.details.rollingProgress, props.details.rollingRequired);
});

// 개별 카테고리 진행률
const categoryProgress = computed(() => {
    if (!hasIndividualRequirements.value) {
        return null;
    }

    const reqs = props.details.individualRollingRequirements!;
    return {
        sport: {
            ...reqs.sport,
            percentage: calculatePercentage(reqs.sport.progress, reqs.sport.required),
        },
        casino: {
            ...reqs.casino,
            percentage: calculatePercentage(reqs.casino.progress, reqs.casino.required),
        },
        minigame: {
            ...reqs.minigame,
            percentage: calculatePercentage(reqs.minigame.progress, reqs.minigame.required),
        },
    };
});

// 출금 가능 여부 메시지
const eligibilityMessage = computed(() => {
    if (props.details.cycleEligible) {
        return "모든 롤링 요구사항을 충족했습니다. 출금이 가능합니다.";
    }
    
    if (hasIndividualRequirements.value) {
        const reqs = props.details.individualRollingRequirements!;
        const eligibleCategories = [];
        const remainingCategories = [];
        
        if (reqs.sport.eligible) eligibleCategories.push("스포츠");
        else remainingCategories.push("스포츠");
        
        if (reqs.casino.eligible) eligibleCategories.push("카지노");
        else remainingCategories.push("카지노");
        
        if (reqs.minigame.eligible) eligibleCategories.push("미니게임");
        else remainingCategories.push("미니게임");
        
        if (eligibleCategories.length > 0) {
            return `${eligibleCategories.join(", ")} 롤링 완료. ${remainingCategories.join(", ")} 롤링이 필요합니다.`;
        } else {
            return "모든 카테고리의 롤링이 필요합니다.";
        }
    }
    
    return "롤링 요구사항을 충족해야 출금이 가능합니다.";
});
</script>

<template>
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <!-- 헤더 -->
            <div class="flex justify-between items-center p-6 border-b">
                <h2 class="text-xl font-bold text-gray-900">쿠폰 롤링 현황</h2>
                <button 
                    @click="handleClose"
                    class="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>

            <!-- 내용 -->
            <div class="p-6">
                <!-- 전체 롤링 진행률 -->
                <div class="mb-6">
                    <div class="flex justify-between items-center mb-2">
                        <span class="text-sm font-medium text-gray-700">전체 롤링 진행률</span>
                        <span class="text-sm font-medium text-gray-900">{{ Math.round(totalProgress) }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                        <div 
                            class="bg-blue-500 h-3 rounded-full transition-all duration-300"
                            :style="{ width: `${totalProgress}%` }"
                        ></div>
                    </div>
                    <div class="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{{ currencyFormat(details.rollingProgress) }}원</span>
                        <span>{{ currencyFormat(details.rollingRequired) }}원</span>
                    </div>
                </div>

                <!-- 개별 카테고리 롤링 (있는 경우) -->
                <div v-if="hasIndividualRequirements && categoryProgress" class="mb-6">
                    <h3 class="text-sm font-medium text-gray-700 mb-3">카테고리별 롤링 현황</h3>
                    
                    <!-- 스포츠 -->
                    <div class="mb-4">
                        <div class="flex justify-between items-center mb-1">
                            <span class="text-xs text-gray-600">스포츠</span>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs text-gray-900">{{ Math.round(categoryProgress.sport.percentage) }}%</span>
                                <span v-if="categoryProgress.sport.eligible" class="text-xs text-green-600 font-medium">완료</span>
                            </div>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2">
                            <div 
                                :class="[
                                    'h-2 rounded-full transition-all duration-300',
                                    categoryProgress.sport.eligible ? 'bg-green-500' : 'bg-blue-500'
                                ]"
                                :style="{ width: `${categoryProgress.sport.percentage}%` }"
                            ></div>
                        </div>
                        <div class="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{{ currencyFormat(categoryProgress.sport.progress) }}원</span>
                            <span>{{ currencyFormat(categoryProgress.sport.required) }}원</span>
                        </div>
                    </div>

                    <!-- 카지노 -->
                    <div class="mb-4">
                        <div class="flex justify-between items-center mb-1">
                            <span class="text-xs text-gray-600">카지노</span>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs text-gray-900">{{ Math.round(categoryProgress.casino.percentage) }}%</span>
                                <span v-if="categoryProgress.casino.eligible" class="text-xs text-green-600 font-medium">완료</span>
                            </div>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2">
                            <div 
                                :class="[
                                    'h-2 rounded-full transition-all duration-300',
                                    categoryProgress.casino.eligible ? 'bg-green-500' : 'bg-purple-500'
                                ]"
                                :style="{ width: `${categoryProgress.casino.percentage}%` }"
                            ></div>
                        </div>
                        <div class="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{{ currencyFormat(categoryProgress.casino.progress) }}원</span>
                            <span>{{ currencyFormat(categoryProgress.casino.required) }}원</span>
                        </div>
                    </div>

                    <!-- 미니게임 -->
                    <div class="mb-4">
                        <div class="flex justify-between items-center mb-1">
                            <span class="text-xs text-gray-600">미니게임</span>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs text-gray-900">{{ Math.round(categoryProgress.minigame.percentage) }}%</span>
                                <span v-if="categoryProgress.minigame.eligible" class="text-xs text-green-600 font-medium">완료</span>
                            </div>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2">
                            <div 
                                :class="[
                                    'h-2 rounded-full transition-all duration-300',
                                    categoryProgress.minigame.eligible ? 'bg-green-500' : 'bg-orange-500'
                                ]"
                                :style="{ width: `${categoryProgress.minigame.percentage}%` }"
                            ></div>
                        </div>
                        <div class="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{{ currencyFormat(categoryProgress.minigame.progress) }}원</span>
                            <span>{{ currencyFormat(categoryProgress.minigame.required) }}원</span>
                        </div>
                    </div>
                </div>

                <!-- 출금 가능 상태 메시지 -->
                <div 
                    :class="[
                        'p-4 rounded-lg text-sm',
                        details.cycleEligible 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                    ]"
                >
                    <div class="flex items-start">
                        <svg 
                            :class="[
                                'w-5 h-5 mr-2 mt-0.5 flex-shrink-0',
                                details.cycleEligible ? 'text-green-500' : 'text-yellow-500'
                            ]" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                        >
                            <path v-if="details.cycleEligible" fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                            <path v-else fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                        </svg>
                        <span>{{ eligibilityMessage }}</span>
                    </div>
                </div>
            </div>

            <!-- 푸터 -->
            <div class="flex justify-end p-6 border-t bg-gray-50">
                <button 
                    @click="handleClose"
                    class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                    닫기
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* 추가 스타일이 필요한 경우 여기에 작성 */
</style>
